/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';
import { SaveCaseService } from 'app/cm/components/case/save-case-service';
import { deepClone } from 'app/shared/lib/utilities';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { ReturnFocusEvent, StoreFocusEvent } from 'app/shared/lib/events';

@autoinject()
export class CaseServiceGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    //private caseId!: string;
    protected services: Array<CM.CaseService> = [];

    private subscriptions: Array<Subscription> = [];
    protected includeInactive: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;
    protected validate = validate;
    protected isValid: boolean = false;
    protected serviceDetail: CM.CaseService | null = null;

    protected canAddService: boolean = true;
    protected canEditService: boolean = false;


    ////****************** Element References ***************************************////
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $serviceForm!: Element;
    protected $pager!: EssPagerCustomElement;
    protected $saveCaseService!: SaveCaseService;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly caseService: CaseService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$modal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        return true;
    }


    ////****************** Functions ************************************************////
    public reload(): void {
        this.checkPermissions();
        this.loadServices()
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canAddService = this.accountContext.hasPermission('CM.CaseService.Insert.AddService');
        this.canEditService = this.accountContext.hasPermission('CM.CaseService.Update.UpdateService');
    }

    protected addService() {
        if(!this.caseId) {
            throw new Error('Missing required property: case id');
        }

        this.serviceDetail = null;
        this.$modal.open();
    }

    protected editService(service: CM.CaseService): void {
        if(!service || !this.$modal) {
            return;
        }
        this.serviceDetail = deepClone(service);
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
    }

    protected findReason(history: CM.CaseServiceHistory[]) {
        const entryWithReason = history.find(historyEntry =>
            historyEntry.metadata?.ended?.timestamp === null
        );
        return entryWithReason?.reason;
    }

    protected getTaskLabel(task?: CM.Task): string {
        if(!task) return '';
        return task.attachedTo?.type?.code === 'CM.CarePlanGoal' ? 'cm:carePlanIntervention.$' : 'core:task.$';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadServices(): Promise<void> {
        if(!this.caseId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive,
        };

        const includes: string[] = [
            this.includeInactive ? 'history:inactive' : 'history',
            'metadata.activeChanged.byUser',
            'metadata.created.byUser',
            'metadata.modified.byUser',
            'modifier',
            'placeOfService',
            'quantity.unit',
            'service',
            'task',
            'task.attachedTo'
        ];

        const fields: string[] = [
            'charges',
            'dateTimeRange.start',
            'dateTimeRange.end',
            'history.metadata.ended.timestamp',
            'history.reason',
            'metadata.activeChanged.byUser.name.*',
            'metadata.activeChanged.timestamp',
            'metadata.created.byUser.name.display',
            'metadata.created.timestamp',
            'metadata.modified.byUser.name.display',
            'metadata.modified.timestamp',
            'modifier.description',
            'placeOfService.description',
            'quantity.unit.code',
            'quantity.unit.description',
            'quantity.value',
            'service.code',
            'service.description',
            'task.attachedTo.type.code',
            'task.description',
            'vendor.identifier.primary',
            'vendor.name.full',
        ];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCaseServices(this.caseId, options, includes, fields);
            this.services = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
            this.events.publish(new ReturnFocusEvent());
        }
    }

    @apiCall
    protected async deactivateService(service: CM.CaseService): Promise<void> {
        if(!service.id) return;

        const result = await confirmation.showDeactivate('cm:service.$', 1, false);
        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.caseService.activateCaseService(service.id, false);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('cm:service.$', false, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async reactivateService(service: CM.CaseService): Promise<void> {
        if(!service.id) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.caseService.activateCaseService(service.id, true);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('cm:service.$', true, isSuccessful);
            this.state.endSave();
        }
    }
}
